import Header from "../components/header/Header";
import {
  Table,
  Card,
  Button,
  message,
  Popconfirm,
  Input,
  Space,
} from "antd";
import { useRef, useState, useEffect } from "react";
import CreateInvoice from "../components/cart/CreateInvoice";
import { useDispatch, useSelector } from "react-redux";
import {
  PlusCircleOutlined,
  MinusCircleOutlined,
  ClearOutlined,
  SearchOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  increase,
  decrease,
  deleteProduct,
  reset,
  selectActiveCart,
} from "../redux/cartSlice";
import Highlighter from "react-highlight-words";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CartPage = () => {
  const { t } = useTranslation("pages");
  const cart = useSelector(selectActiveCart);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [isValidatingStock, setIsValidatingStock] = useState(false);

  /* ---------------- SEARCH ---------------- */
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={t("searchColumn", { column: dataIndex })}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() =>
              handleSearch(selectedKeys, confirm, dataIndex)
            }
          >
            {t("search")}
          </Button>
          <Button
            size="small"
            onClick={() => clearFilters && handleReset(clearFilters)}
          >
            {t("reset")}
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            {t("close")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  /* ---------------- TABLE COLUMNS ---------------- */
  const columns = [
    {
      title: t("productImage"),
      dataIndex: "img",
      render: (img) => (
        <div className="w-full h-20 flex items-center justify-center bg-gray-100 rounded">
          {img ? (
            <img
              src={img}
              alt=""
              className="w-full h-full object-cover rounded"
              onError={(e) => {
                e.target.style.display = "none";
                const iconDiv = e.target.parentElement.querySelector(".default-icon");
                if (iconDiv) iconDiv.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center default-icon ${
              img ? "hidden" : ""
            }`}
          >
            <PictureOutlined className="text-3xl text-gray-400" />
          </div>
        </div>
      ),
    },
    {
      title: t("productName"),
      dataIndex: "title",
      ...getColumnSearchProps("title"),
    },
    {
      title: t("category"),
      dataIndex: "category",
      ...getColumnSearchProps("category"),
    },
    {
      title: t("price"),
      dataIndex: "price",
      sorter: (a, b) => a.price - b.price,
      render: (price) => Number(price || 0).toFixed(2),
    },
    {
      title: t("availableStock"),
      render: (_, record) => {
        const availableStock = record.totalStock ?? "N/A";
        const isLowStock = typeof availableStock === "number" && availableStock < record.quantity;
        return (
          <span className={isLowStock ? "text-red-600 font-semibold" : "text-gray-700"}>
            {availableStock}
            {isLowStock && " ⚠️"}
          </span>
        );
      },
    },
    {
      title: t("quantity"),
      render: (_, record) => {
        const availableStock = record.totalStock ?? null;
        const isLowStock = typeof availableStock === "number" && availableStock < record.quantity;
        return (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              shape="circle"
              icon={<PlusCircleOutlined />}
              onClick={() => dispatch(increase(record))}
              disabled={typeof availableStock === "number" && availableStock <= record.quantity}
            />
            <b className={isLowStock ? "text-red-600 font-semibold" : "text-gray-800 font-medium"}>{record.quantity}</b>
            <Button
              size="small"
              shape="circle"
              icon={<MinusCircleOutlined />}
              onClick={() => {
                if (record.quantity === 1) {
                  if (window.confirm(t("deleteProductConfirmShort"))) {
                    dispatch(decrease(record));
                  }
                } else {
                  dispatch(decrease(record));
                }
              }}
            />
          </div>
        );
      },
    },
    {
      title: t("tax"),
      render: (_, record) =>
        Number(record.tax || 0).toFixed(2),
    },
    {
      title: t("total"),
      render: (_, record) =>
        Number(record.totalPrice || 0).toFixed(2),
    },
    {
      title: t("action"),
      render: (_, record) => (
        <Popconfirm
          title={t("deleteProductConfirmShort")}
          onConfirm={() => dispatch(deleteProduct(record))}
        >
          <Button danger>{t("delete")}</Button>
        </Popconfirm>
      ),
    },
  ];

  /* ---------------- STOCK VALIDATION (from cart item totalStock) ---------------- */
  const validateStock = () => {
    if (!cart.cartItems.length) {
      message.warning(t("cartEmpty"));
      return false;
    }

    setIsValidatingStock(true);
    // Validate each cart item against its own totalStock
    const stockErrors = [];
    const missingProducts = [];

    for (const cartItem of cart.cartItems) {
      const currentStock =
        typeof cartItem.totalStock === "number" ? cartItem.totalStock : null;

      if (currentStock === null) {
        missingProducts.push(cartItem.title || cartItem._id);
        continue;
      }

      if (currentStock < cartItem.quantity) {
        stockErrors.push({
          productName: cartItem.title || cartItem._id,
          requested: cartItem.quantity,
          available: currentStock,
        });
      }
    }

    if (missingProducts.length > 0) {
      message.error(
        `${t("someProductsMissingStock")} ${missingProducts.join(", ")}`
      );
      setIsValidatingStock(false);
      return false;
    }

    if (stockErrors.length > 0) {
      const errorMessages = stockErrors.map(
        (error) =>
          `${error.productName}: ${t("requested")} ${error.requested}, ${t("available")} ${error.available}`
      );

      message.error({
        content: (
          <div>
            <div style={{ marginBottom: 8, fontWeight: "bold" }}>
              {t("insufficientStock")}
            </div>
            {errorMessages.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: 4 }}>
                • {msg}
              </div>
            ))}
          </div>
        ),
        duration: 8,
      });

      setIsValidatingStock(false);
      return false;
    }

    setIsValidatingStock(false);
    return true;
  };

  const handleCreateOrder = () => {
    const isValid = validateStock();
    if (isValid) {
      setIsModalOpen(true);
    }
  };

  const [autoSubmitInvoice, setAutoSubmitInvoice] = useState(false);
  const [autoFocusCustomer, setAutoFocusCustomer] = useState(false);

  /* ---------------- AUTO CHECKOUT (from basket) ---------------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldCheckout = params.get("checkout") === "1";
    const shouldAutoSubmit = params.get("autoSubmit") === "1";
    const shouldFocusCustomer = params.get("focusCustomer") === "1";

    if (!shouldCheckout) return;
    if (!cart.cartItems.length) return;
    if (isModalOpen || isValidatingStock) return;

    if (shouldFocusCustomer) setAutoFocusCustomer(true);

    // Set auto-submit flag BEFORE opening modal to ensure it's ready
    if (shouldAutoSubmit) {
      setAutoSubmitInvoice(true);
      // Small delay to ensure state update is processed before opening modal
      requestAnimationFrame(() => {
        setTimeout(() => {
          handleCreateOrder();
        }, 50);
      });
    } else {
      handleCreateOrder();
    }

    // Remove query param so refresh/back doesn't re-trigger
    navigate("/cart", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, cart.cartItems.length]);

  /* ---------------- TOTALS ---------------- */
  const subTotal = cart.cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const totalTax = cart.cartItems.reduce(
    (sum, item) => sum + (item.tax || 0),
    0
  );

  /* ---------------- UI ---------------- */
  return (
    <>
      <Header />

      <div className="px-6">
        <Table
          rowKey="_id"
          dataSource={cart.cartItems}
          columns={columns}
          bordered
          pagination={false}
          scroll={{ y: 400 }}
        />

        <div className="flex justify-end mt-4">
          <Card className="w-80">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">{t("subtotal")}</span>
              <span className="text-gray-800">Rs {subTotal.toFixed(2)}</span>
            </div>

            <div className="flex justify-between my-2 text-sm">
              <span className="text-gray-700">{t("tax")}</span>
              <span className="text-red-600">
                + Rs {totalTax.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="font-semibold text-gray-800">{t("total")}</span>
              <span className="font-semibold text-gray-800">Rs {Number(cart.total || 0).toFixed(2)}</span>
            </div>

            <Button
              type="primary"
              className="w-full mt-4"
              disabled={!cart.cartItems.length || isValidatingStock}
              loading={isValidatingStock}
              onClick={handleCreateOrder}
            >
              {t("createInvoice")}
            </Button>

            <Popconfirm
              title={t("clearCart")}
              onConfirm={() => dispatch(reset())}
            >
              <Button
                danger
                type="default"
                className="w-full mt-2"
                disabled={!cart.cartItems.length}
                icon={<ClearOutlined />}
              >
                {t("deleteAll")}
              </Button>
            </Popconfirm>
          </Card>
        </div>
      </div>

      <CreateInvoice
        isModalOpen={isModalOpen}
        setIsModalOpen={(open) => {
          setIsModalOpen(open);
          if (!open) {
            setAutoSubmitInvoice(false);
            setAutoFocusCustomer(false);
          }
        }}
        autoSubmit={autoSubmitInvoice}
        autoFocusCustomer={autoFocusCustomer}
      />
    </>
  );
};

export default CartPage;
